import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, TrashIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';

const CartSidebar = () => {
  const { 
    items, 
    isOpen, 
    closeCart, 
    removeFromCart, 
    updateQuantity, 
    getCartSummary 
  } = useCart();

  const { subtotal, tax, shipping, total, itemCount } = getCartSummary();

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeCart}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md" dir="rtl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    <div className="flex-1 overflow-y-auto py-6 px-4 sm:px-6">
                      <div className="flex items-start justify-between">
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          עגלת קניות ({itemCount})
                        </Dialog.Title>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="-m-2 p-2 text-gray-400 hover:text-gray-500"
                            onClick={closeCart}
                          >
                            <span className="sr-only">סגור עגלה</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-8">
                        <div className="flow-root">
                          <ul className="-my-6 divide-y divide-gray-200">
                            {items.length === 0 ? (
                              <div className="text-center py-8">
                                <p className="text-gray-500">העגלה ריקה</p>
                                <Link
                                  to="/products"
                                  onClick={closeCart}
                                  className="btn btn-primary mt-4"
                                >
                                  התחל לקנות
                                </Link>
                              </div>
                            ) : (
                              items.map((item) => {
                                // Check if this is a bundle or a regular product
                                const isBundle = item.is_bundle || item.bundle;
                                const itemData = isBundle ? item.bundle : item.product;
                                
                                // Safety check - skip if itemData is null
                                if (!itemData) {
                                  console.warn('Item data is null:', item);
                                  return null;
                                }
                                
                                const itemName = isBundle ? itemData.title : itemData.name;
                                const itemImage = itemData.images?.[0] || itemData.bundle_image;
                                const itemLink = isBundle ? `/bundles/${itemData.id}` : `/products/${itemData.id}`;
                                const itemPrice = isBundle ? itemData.bundle_price : (itemData.price || itemData.price_b2c);
                                
                                return (
                                <li key={item.id} className="flex py-6">
                                  <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                                    <img
                                      src={itemImage ? (itemImage.startsWith('http') ? `${itemImage}?id=${itemData.id}` : `/api${itemImage}?id=${itemData.id}`) : 'https://picsum.photos/400/400'}
                                      alt={itemName}
                                      className="h-full w-full object-cover object-center"
                                    />
                                  </div>

                                  <div className="ml-4 flex flex-1 flex-col">
                                    <div>
                                      <div className="flex justify-between text-base font-medium text-gray-900">
                                        <h3>
                                          <Link 
                                            to={itemLink}
                                            onClick={closeCart}
                                          >
                                            {itemName}
                                            {isBundle && <span className="text-xs text-primary-600 mr-2">חבילה</span>}
                                          </Link>
                                        </h3>
                                        <p className="mr-4">
                                          <span>
                                            ₪{(parseFloat(itemPrice || 0) * item.quantity).toFixed(2)}
                                            {isBundle && item.bundle_discount > 0 && (
                                              <span className="text-xs text-green-600 block">
                                                חסכת ₪{(item.bundle_discount * item.quantity).toFixed(2)}
                                              </span>
                                            )}
                                          </span>
                                        </p>
                                      </div>
                                      {(itemData.bundle_sku || itemData.sku) && (
                                        <p className="mt-1 text-sm text-gray-500">
                                          {isBundle ? itemData.bundle_sku : itemData.sku}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex flex-1 items-end justify-between text-sm">
                                      <div className="flex items-center">
                                        <button
                                          type="button"
                                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                          className="p-1 text-gray-400 hover:text-gray-500"
                                        >
                                          <MinusIcon className="h-4 w-4" />
                                        </button>
                                        <span className="mx-2 text-gray-500">
                                          כמות: {item.quantity}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                          className="p-1 text-gray-400 hover:text-gray-500"
                                        >
                                          <PlusIcon className="h-4 w-4" />
                                        </button>
                                      </div>

                                      <div className="flex">
                                        <button
                                          type="button"
                                          onClick={() => removeFromCart(item.id)}
                                          className="p-1 text-red-500 hover:text-red-600"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              );
                              })
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {items.length > 0 && (
                      <div className="border-t border-gray-200 bg-gray-50 py-6 px-4 sm:px-6 sticky bottom-0">
                        <div className="space-y-2 text-sm mb-4">
                          <div className="flex justify-between">
                            <span className="text-gray-600">סכום ביניים</span>
                            <span className="font-medium">₪{subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">מע"מ</span>
                            <span className="font-medium">₪{tax.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">משלוח</span>
                            <span className="font-medium">{shipping === 0 ? <span className="text-green-600">חינם</span> : `₪${shipping.toFixed(2)}`}</span>
                          </div>
                          <div className="flex justify-between border-t-2 border-gray-300 pt-3 mt-3">
                            <span className="text-lg font-bold text-gray-900">סה"כ לתשלום</span>
                            <span className="text-xl font-bold text-primary-600">₪{total.toFixed(2)}</span>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <Link
                            to="/fast-checkout"
                            onClick={closeCart}
                            className="block w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-6 rounded-lg text-center transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                          >
                            <span className="flex items-center justify-center gap-2">
                              <span>מעבר לתשלום</span>
                              <span className="text-lg">→</span>
                            </span>
                          </Link>
                          
                          <button
                            type="button"
                            onClick={closeCart}
                            className="w-full text-primary-600 hover:text-primary-700 font-medium py-2 text-center transition-colors"
                          >
                            המשך לקנות
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CartSidebar;
